import { ModalityCodesEnum } from '@octano/global-ui';
import { UpdateTeacherOrAssistantRow } from '../views/SectionsMaintainer/types';
import { AccountType } from './account';
import { Campus } from './campus';
import { PaginationRequestType } from './paginationRequestType';
import { Schedule } from './schedule';
import { Module } from './sectionSchedules';

export enum SectionScheduleResponsibleTypes {
  TEACHER = 'profesor',
  ASSISTANT = 'ayudante',
}

export enum LessonsPeriodicity {
  Weekly = 'Semanal',
  BiWeekly = 'Bisemanal',
  Monthly = 'Mensual',
}

export interface SectionMaintainerListType {
  id: number;
  name: string;
  maximumStudent: number;
  closedAt: string | null;
  closedWithId: number | null;
  period: {
    id: number;
    code: string;
    name: string;
    active: boolean;
    creditMax: number;
    creditMin: number;
    description: string;
    startDate: string;
    endDate: string;
    loadingDeadline: null;
  };
  campus: {
    id: number;
    code: string;
    name: string;
  };
  course: {
    id: number;
    code: string;
    credits: number;
    name: string;
    shortening: string;
    school?: {
      id: number;
      code: string;
      name: string;
    };
  };
  professors: {
    id: number;
    account: {
      id: number;
      fullName: string;
      maternalLastName: string;
      name: string;
      paternalLastName: string;
      paternalPreference: boolean;
    };
  }[];
}

export type SectionMaintainerListResponse = PaginationRequestType<SectionMaintainerListType>;

export type SectionDetail = {
  id: number;
  name: string;
  maximumStudent: number;
  onlineAssistanceRequirement: number;
  periodId: number;
  course: {
    id: number;
    code: string;
    name: string;
    credits: number;
    shortening: string;
    school?: {
      id: number;
      code: string;
      name: string;
      description: string;
    };
    modality?: {
      deliveryMode: ModalityCodesEnum;
    };
  };
  campus: Campus;
  professors: (Teacher & {
    isOnline?: boolean;
    isPresential?: boolean;
    order?: number;
  })[];
  assistant: (Teacher & {
    isOnline?: boolean;
    isPresential?: boolean;
    order?: number;
  })[];
  assistanceRequirements: Array<{
    id?: number;
    minPercentage: number;
    activityTypeId: number;
    sectionId: number;
    createdAt?: Date;
    updatedAt?: Date;
    activityType: {
      id: number;
      name: string;
    };
  }>;
  period: {
    id: number;
    code: string;
    name: string;
    active: boolean;
    creditMax: number;
    creditMin: number;
    description: string;
    startDate: string;
    endDate: string;
    loadingDeadline: null;
  };
  schedule?: Schedule;
};

export type Teacher = {
  id: number;
  account?: AccountType;
};

export type SectionSchedule = {
  id: number;
  day: string;
  activityId: number;
  activity: {
    id: number;
    name: string;
  };
  modality:
    | {
        id: number;
        name: string;
        deliveryMode: ModalityCodesEnum;
      }
    | undefined;
  classroom: {
    id: number;
    name: string;
    campus: Campus | null;
  };
  startModule: Module;
  endModule: Module | null;
  startDate: string | null;
  endDate: string | null;
  lessonsPeriodicity?: LessonsPeriodicity;
  deletedAt: string;
  responsibles: {
    id: number;
    accountId: number;
    account: AccountType;
    typeOfResponsible: SectionScheduleResponsibleTypes;
  }[];
};

export type ResponsibleAssistant = {
  id: number;
  name: string;
  paternalLastName: string;
  maternalLastName: string;
  paternalPreference: boolean;
  fullName: string;
  run: string | null;
  passportNumber: string | null;
  countryId: string | null;
  email: string;
  birthday: string;
  isActive: boolean;
  personalEmail: string | null;
  studentId: number;
};

export type PeriodType = {
  id: number;
  code: string;
  name: string;
  description: string;
  active: boolean;
  startDate: string;
  endDate: string;
  creditMin: number;
  creditMax: number;
  lastLoadDate: string | null;
  loadingDeadline: string | null;
  academicYear: number;
};

export type SchoolType = {
  id: number;
  code: string;
  name: string;
  description: string;
};

export type StudyPlanType = {
  id: number;
  code: string;
  name: string;
};

export type CourseType = {
  id: number;
  name: string;
  code: string;
  shortening: string;
  credits: number;
  modality?: {
    name: string;
  };
};

export type FormDataResponseType = {
  teachers: Teacher[];
  studyPlans: StudyPlanType[];
  periods: PeriodType[];
  schools: SchoolType[];
  campuses: Campus[];
  schedules: Schedule[];
};

export type CourseListResponseType = CourseType[];

export type CreateSectionBody = {
  maximumStudent: number;
  campusId: number;
  professors: UpdateTeacherOrAssistantRow[];
  assistants: UpdateTeacherOrAssistantRow[];
  courseCode: string;
  periodId: number;
  name: string;
  scheduleId?: number;
};

export type AsignPackageToSectionBody = {
  packagesIds: number[];
};
